import React, { useEffect, useRef, useState } from 'react'
import { Bar, Pie } from 'react-chartjs-2'
import { groupableHorizontalChartOptions, groupableVerticalChartOptions, horizontalChartOptions, pieChartOptions, stringChartOptions, verticalChartOptions } from './WidgetOptions'
import { useTranslation } from 'react-i18next';
import { CircularProgress, ClickAwayListener, Fade, Paper, Popper } from '@material-ui/core';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import OpenWithIcon from '@material-ui/icons/OpenWith';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import DashBoardService from '../../../../../services/DashBoardService/DashBoardService';
import { Chart } from 'chart.js';

const dynamicLegendHeightPlugin = {
    id: 'dynamicLegendHeight',
    afterRender: function (chart) {
        // Use a flag to check if the legend size has already been calculated
        if (chart._legendSizeCalculated) {
            return;
        }

        const calculateLegendSize = () => {
            const canvasHeight = chart.canvas.height;
            const canvasWidth = chart.canvas.width;

            // Calculate the font and box size based on canvas dimensions
            const fontSize = Math.max(Math.min(canvasHeight, canvasWidth) * 0.02, 8); // Min font size 8, max 2% of canvas size
            const boxSize = fontSize; // Box size equal to font size

            // Update legend label sizes if necessary
            if (
                chart.options.plugins.legend.labels.fontSize !== fontSize ||
                chart.options.plugins.legend.labels.boxWidth !== boxSize
            ) {
                chart.options.plugins.legend.labels.fontSize = fontSize;
                chart.options.plugins.legend.labels.boxWidth = boxSize;
                chart.options.plugins.legend.labels.boxHeight = boxSize; // Supported in Chart.js 3.7.0+

                // Mark that the legend size has been calculated to prevent infinite loop
                chart._legendSizeCalculated = true;

                chart.update();
            }
        };

        // Calculate initially
        calculateLegendSize();
    },
};

// Register the plugin
Chart.register(dynamicLegendHeightPlugin);

const Widget = ({ item, handleAddWidget, handleOpenRemoveWidget, openConfigure, label, moveWidget, dateType, customDate, id }) => {
    const [fontSize, setFontSize] = useState('120px');
    const [titleFontSize, setTitleFontSize] = useState('40px');
    const [widgetData, setWidgetData] = useState({});
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const ref = useRef(null);
    const stringWidgetRef = useRef(null);
    const spanRef = useRef(null);
    const titleRef = useRef(null);

    const [{ opacity }, drag, preview] = useDrag({
        item: {
            id: id ? id : "", type: 'widget'
        },
        canDrag: () => openConfigure,
        collect: (monitor) => ({
            opacity: monitor.isDragging() ? 0 : 1,
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: 'widget',
        drop: (draggedItem, monitor) => {
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;

            if (draggedItem.id !== id) {
                if (hoverClientX < hoverMiddleX) {
                    moveWidget(draggedItem.id, id, 'left');
                } else {
                    moveWidget(draggedItem.id, id, 'right');
                }
            }
            // Taşıma işlemi tamamlandıktan sonra rengi sıfırla
            ref.current.style.background = "#FFFFFF";
        },
        hover: (draggedItem, monitor) => {
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;

            if (draggedItem.id !== id) {
                const divElement = ref.current;
                if (hoverClientX < hoverMiddleX) {
                    divElement.style.background = `linear-gradient(to right, #ADD8E6 50%, #FFFFFF 50%)`;
                } else {
                    divElement.style.background = `linear-gradient(to left, #ADD8E6 50%, #FFFFFF 50%)`;
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    useEffect(() => {
        fetchWidgetData()
    }, []);

    const fetchWidgetData = async () => {
        const body = {
            date_type: dateType,
            custom_date: customDate
        }
        const response = await DashBoardService.getWidgetDataById(item?.id, body)
        if (response.status) {
            if (response.data) {
                setWidgetData((pre) => {
                    return identifyWidgets(response.data)
                })
            }
        }
    }

    // Hover durumunda değilse arka plan rengini sıfırla
    useEffect(() => {
        if (!isOver) {
            ref.current.style.background = "#FFFFFF";
        }
    }, [isOver]);

    useEffect(() => {
        const resizeFont = () => {
            const divWidth = stringWidgetRef.current.offsetWidth;
            const divHeight = stringWidgetRef.current.offsetHeight;
            let newFontSize = parseInt(fontSize, 10);
            spanRef.current.style.fontSize = `${newFontSize}px`;

            // Font boyutunu genişlik veya yükseklik sığana kadar ayarlıyoruz
            while (
                (spanRef.current.offsetWidth > (divWidth / 2) || spanRef.current.offsetHeight > divHeight / (10 / 8)) &&
                newFontSize > 0
            ) {
                newFontSize -= 1;
                spanRef.current.style.fontSize = `${newFontSize / (3 / 2)}px`;
            }

            let newTitleFontSize = parseInt(titleFontSize, 10);
            titleRef.current.style.fontSize = `${newTitleFontSize}px`;

            while (
                (titleRef.current.offsetWidth > (divWidth / 2) || titleRef.current.offsetHeight > divHeight / (10 / 2)) &&
                newFontSize > 0
            ) {
                newFontSize -= 1;
                titleRef.current.style.fontSize = `${newFontSize / (3 / 2)}px`;
            }
            // setFontSize(`${newFontSize / (3 / 2)}px`);
        };

        // İlk yükleme ve pencere boyutu değişikliklerinde çalıştır
        if (item.widget.name === "string chart") {
            if (spanRef.current && titleRef.current) {
                resizeFont();
            }
            // window.addEventListener('resize', resizeFont);

            // return () => {
            //     window.removeEventListener('resize', resizeFont);
            // };
        }
    }, [item, ref.current?.offsetHeight, ref.current?.offsetWidth, widgetData?.content]);


    drag(drop(ref));
    preview(ref);

    const open = Boolean(anchorEl);

    function handleClose(event) {
        if (anchorEl && anchorEl.contains(event.target)) {
            return;
        }
        setAnchorEl(null);
    }

    function handleClick(event) {
        if (anchorEl) { setAnchorEl(null); }
        else { setAnchorEl(event.currentTarget); }
    }

    const handleFindWidget = (widget) => {
        let data = ""
        const chart = widget?.data
        if (chart) {
            switch (widget.name) {
                case "horizontal bar":
                    data = <Bar key={`${widget.id}_${widget.name}`} data={chart} options={widget.options} className='db-widget-canvas' />
                    break;
                case "vertical bar":
                    data = <Bar key={`${widget.id}_${widget.name}`} data={chart} options={widget.options} className='db-widget-canvas' />
                    break;
                case "groupable vertical bar":
                    data = <Bar key={`${widget.id}_${widget.name}`} data={chart} options={widget.options} className='db-widget-canvas' />
                    break;
                case "groupable horizontal bar":
                    data = <Bar key={`${widget.id}_${widget.name}`} data={chart} options={widget.options} className='db-widget-canvas' />
                    break;
                case "pie chart":
                    data = <Pie key={`${widget.id}_${widget.name}`} data={chart} options={widget.options} className='db-widget-canvas' />
                    break;
                case "string chart":
                    data = <div ref={stringWidgetRef} key={`${widget.id}_${widget.name}`} style={stringChartOptions.container}>
                        <div ref={titleRef} style={stringChartOptions.stringText}>{chart?.header}</div>
                        <span ref={spanRef} className='db-string-chart-main-value-span' style={{ fontSize: "120px", color: `${chart.color ? chart.color : ""}` }}>{chart.currentValue}</span>
                        {/* <div style={stringChartOptions.valueContainer}>
                            {chart.beforeContent ? <span style={{ color: `${chart.beforeContent.color ? chart.beforeContent.color : "black"}`, fontSize: `${chart.beforeContent?.size ? chart.beforeContent.size : "1em"}` }}>{chart.beforeContent?.text ? chart.beforeContent?.text : ""}</span> : ""}
                            {chart.afterContent ? <span style={{ color: `${chart.afterContent.color ? chart.afterContent.color : "black"}`, fontSize: `${chart.afterContent?.size ? chart.afterContent.size : "1em"}` }}>{chart.afterContent?.text ? chart.afterContent?.text : ""}</span> : ""}
                        </div> */}
                        {/* <div style={stringChartOptions.changeText}>
                            <span style={stringChartOptions.percentageChange}>
                                {chart.percentageChange}{chart.previousPeriod.before ? chart.previousPeriod.before : ""}
                            </span>
                            <span>{chart.previousPeriod.after ? chart.previousPeriod.after : ""}</span>
                        </div> */}
                    </div>
                    break;
                default:
                    break;
            }
        }
        return data
    }

    const formatTime = (milliseconds) => {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600); // Toplam saat
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${hours}h ${minutes}m ${seconds}s`;
    };

    const identifyWidgets = (widget) => {
        const columnValues = widget.column_values?.length ? widget.column_values.map((value) => {
            return Object.values(value)
        }) : []
        const translationColumns = widget.column_names.map((columnName) => {
            return t(label + columnName)
        }
        )
        switch (item.widget?.name) {
            case "vertical bar":
                return {
                    content: {
                        name: "vertical bar",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            labels: translationColumns,
                            datasets: [
                                {
                                    // label: 'Time (hrs)',
                                    data: columnValues[0],
                                    backgroundColor: widget.column_colors.map((color, index) => {
                                        return color ? color : getColorByIndex(index)
                                    }), // Çubukların rengi
                                    borderColor: widget.column_colors.map((color, index) => {
                                        return color ? color : getColorByIndex(index)
                                    }),
                                    borderWidth: 1,
                                    borderRadius: 5, // Çubuklara köşe yarıçapı ekleyin
                                    // barThickness: 40, // Çubuk genişliği
                                },
                            ],
                        },
                        options: {
                            ...verticalChartOptions, // Mevcut optionsları al
                            scales: {
                                ...verticalChartOptions.scales,
                                y: {
                                    ...verticalChartOptions.scales.y,
                                    ticks: {
                                        callback: function (value) {
                                            return widget.column_types[0] === "milisecond" ? formatTime(value) : value; // Format y-axis labels
                                        },
                                    },
                                },
                            },
                            plugins: {
                                ...verticalChartOptions.plugins,
                                tooltip: {
                                    ...verticalChartOptions.plugins?.tooltip,
                                    callbacks: {
                                        label: function (tooltipItem) {
                                            return widget.column_types[tooltipItem.dataIndex] === "milisecond" ? formatTime(tooltipItem.raw) : tooltipItem.raw; // Format tooltip labels
                                        },
                                    },
                                },
                                title: {
                                    display: true,
                                    text: widget.display_name ? widget.display_name : widget.report_label ? t(label + widget.report_label) : 'Vertical Chart', // Dinamik başlık
                                }
                            },
                        }
                    }, cols: widget.column, rows: widget.row
                }
                break;
            case "horizontal bar":
                return {
                    content: {
                        name: "horizontal bar",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            labels: translationColumns,
                            datasets: [
                                {
                                    data: columnValues[0],
                                    backgroundColor: widget.column_colors.map((color, index) => {
                                        return color ? color : getColorByIndex(index)
                                    }),
                                    borderRadius: 10,
                                },
                            ],
                        },
                        options: {
                            ...horizontalChartOptions, // Mevcut optionsları al
                            scales: {
                                ...horizontalChartOptions.scales,
                                x: {
                                    ...horizontalChartOptions.scales.y,
                                    ticks: {
                                        callback: function (value) {
                                            return widget.column_types[0] === "milisecond" ? formatTime(value) : value; // Format y-axis labels
                                        },
                                    },
                                },
                            },
                            plugins: {
                                ...horizontalChartOptions.plugins,
                                tooltip: {
                                    ...horizontalChartOptions.plugins?.tooltip,
                                    callbacks: {
                                        label: function (tooltipItem) {
                                            return widget.column_types[tooltipItem.dataIndex] === "milisecond" ? formatTime(tooltipItem.raw) : tooltipItem.raw; // Format tooltip labels
                                        },
                                    },
                                },
                                title: {
                                    display: true,
                                    text: widget.display_name ? widget.display_name : widget.report_label ? t(label + widget.report_label) : 'Horizontal Chart', // Dinamik başlık
                                },
                            },
                        }
                    }, cols: widget.column, rows: widget.row
                }
                break;
            case "groupable vertical bar":
                return {
                    content: {
                        name: "groupable vertical bar",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            labels: translationColumns, // columnlar
                            datasets: widget.groups.map((group, index) => {
                                return {
                                    label: group,
                                    data: columnValues[index], // veriler
                                    // backgroundColor: generateVibrantColor(),
                                    backgroundColor: generateColorArray(false, index),
                                }
                            }),
                        },
                        options: {
                            ...groupableVerticalChartOptions, // Mevcut optionsları al
                            plugins: {
                                ...groupableVerticalChartOptions.plugins,
                                legend: {
                                    ...groupableVerticalChartOptions.plugins.legend,
                                    display: widget.groups.length <= 20, // Hide legend if groups > 20
                                    labels: {
                                        generateLabels: function (chart) {
                                            const originalLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                                            const maxLength = 15; // max length for legend widget

                                            return originalLabels.map(label => {
                                                if (label.text.length > maxLength) {
                                                    label.text = label.text.substring(0, maxLength) + '...';
                                                }
                                                return label;
                                            });
                                        },
                                        boxWidth: 20, // İkonun genişliği
                                    },
                                },
                                tooltip: {
                                    ...groupableVerticalChartOptions.plugins?.tooltip,
                                    callbacks: {
                                        label: function (context) {
                                            const groupName = context.dataset.label || '';
                                            const value = context.raw;
                                            const maxLength = 20; // max length for tooltip text

                                            // Eğer grup adı uzun ise kes ve sonuna '...' ekle
                                            const truncatedGroupName = groupName.length > maxLength ? groupName.substring(0, maxLength) + '...' : groupName;

                                            return `${truncatedGroupName}: ${widget.column_types[context.dataIndex] === "milisecond" ? formatTime(value) : value}`;
                                        },
                                    },
                                },
                                title: {
                                    display: true,
                                    text: widget.display_name ? widget.display_name : widget.report_label ? t(label + widget.report_label) : 'Groupable Horizontal Chart', // Dinamik başlık
                                },
                                dynamicLegendHeightPlugin
                            },
                            scales: {
                                ...groupableVerticalChartOptions.scales,
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function (value) {
                                            return widget.column_types[0] === "milisecond" ? formatTime(value) : value; // Format y-axis labels
                                        }
                                    }
                                },
                            },
                        }
                    }, cols: widget.column, rows: widget.row
                }
            case "groupable horizontal bar":
                return {
                    content: {
                        name: "groupable horizontal bar",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            labels: translationColumns, // columnlar
                            datasets: widget.groups.map((group, index) => {
                                return {
                                    label: group,
                                    data: columnValues[index], // veriler
                                    // backgroundColor: generateVibrantColor(),
                                    backgroundColor: generateColorArray(false, index),
                                }
                            }),
                        },
                        options: {
                            ...groupableHorizontalChartOptions, // Mevcut optionsları al
                            plugins: {
                                ...groupableHorizontalChartOptions.plugins,
                                legend: {
                                    ...groupableVerticalChartOptions.plugins.legend,
                                    display: widget.groups.length <= 20, // Hide legend if groups > 20
                                    labels: {
                                        generateLabels: function (chart) {
                                            const originalLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                                            const maxLength = 15; // max length for legend widget

                                            return originalLabels.map(label => {
                                                if (label.text.length > maxLength) {
                                                    label.text = label.text.substring(0, maxLength) + '...';
                                                }
                                                return label;
                                            });
                                        },
                                        boxWidth: 20, // İkonun genişliği
                                    },
                                },
                                tooltip: {
                                    ...groupableHorizontalChartOptions.plugins?.tooltip,
                                    callbacks: {
                                        label: function (context) {
                                            const groupName = context.dataset.label || '';
                                            const value = context.raw;
                                            const maxLength = 20; // max length for tooltip text

                                            // Eğer grup adı uzun ise kes ve sonuna '...' ekle
                                            const truncatedGroupName = groupName.length > maxLength ? groupName.substring(0, maxLength) + '...' : groupName;

                                            return `${truncatedGroupName}: ${widget.column_types[context.dataIndex] === "milisecond" ? formatTime(value) : value}`;
                                        },
                                    },
                                },
                                title: {
                                    display: true,
                                    text: widget.display_name ? widget.display_name : widget.report_label ? t(label + widget.report_label) : 'Groupable Horizontal Chart', // Dinamik başlık
                                },
                                dynamicLegendHeightPlugin
                            },
                            scales: {
                                ...groupableHorizontalChartOptions.scales,
                                x: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function (value) {
                                            return widget.column_types[0] === "milisecond" ? formatTime(value) : value; // Format y-axis labels
                                        }
                                    }
                                },
                            },
                        }
                    }, cols: widget.column, rows: widget.row
                }
            case "pie chart":
                return {
                    content: {
                        name: "pie chart",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            labels: translationColumns,
                            datasets: [
                                {
                                    label: 'Categories',
                                    data: columnValues[0], // Buradaki veriler slice büyüklüğünü belirler
                                    // backgroundColor: translationColumns.map(() => generateVibrantColor()),
                                    backgroundColor: widget.column_colors.map((color, index) => {
                                        return color ? color : getColorByIndex(index)
                                    }),
                                    borderColor: widget.column_colors.map((color, index) => {
                                        return color ? color : getColorByIndex(index)
                                    }),
                                    borderWidth: 1,
                                },
                            ],
                        },
                        options: {
                            ...pieChartOptions, // Mevcut optionsları al
                            plugins: {
                                ...pieChartOptions.plugins,
                                title: {
                                    display: true,
                                    text: widget.display_name ? widget.display_name : widget.report_label ? t(label + widget.report_label) : 'Pie Chart', // Dinamik başlık
                                },
                                tooltip: {
                                    callbacks: {
                                        label: function (tooltipItem) {
                                            return widget.column_types[tooltipItem.dataIndex] === "milisecond" ? formatTime(tooltipItem.raw) : tooltipItem.raw; // Format tooltip labels
                                        },
                                    },
                                },
                                dynamicLegendHeightPlugin
                            }
                        }
                    }, cols: widget.column, rows: widget.row
                }
                break;
            case "string chart":
                return {
                    content: {
                        name: "string chart",
                        id: widget.id,
                        order: widget?.order,
                        data: {
                            header: widget.display_name ? widget.display_name : translationColumns[0],
                            currentValue: widget.column_types[0] === "milisecond" ? formatTime(columnValues[0]) : columnValues[0],
                            color: "#DC9B3B",
                            // percentageChange: 483,
                            previousPeriod: {
                                // before: "% Higher than ",
                                // after: 'your previous fiscal year'
                            },
                            beforeContent: {
                                // text: "$",
                                // color: "#DC9B3B4F"
                            },
                            afterContent: {
                                // text: "count",
                                // color: "#DC9B3B4F"
                            },
                        },
                    }, cols: widget.column, rows: widget.row
                }
                break;

            default:
                break;
        }
    }


    function generateMutedColor() {
        const r = Math.floor(Math.random() * 100) + 100; // 100 ile 199 arasında
        const g = Math.floor(Math.random() * 100) + 100; // 100 ile 199 arasında
        const b = Math.floor(Math.random() * 100) + 100; // 100 ile 199 arasında

        return `rgb(${r}, ${g}, ${b})`;
    }

    function getColorByIndex(index) {
        const colors = [
            "#A1C6EA", "#FFC4A3", "#B6E3E9", "#FFABAB", "#D4A5A5",
            "#C5E1A5", "#FFD5C2", "#A3D5FF", "#D7D1E6", "#C1E1C5",
            "#FFB5E8", "#FFDAC1", "#FF9AA2", "#FFCCF9", "#FDC4E8",
            "#FFFACD", "#FFDFD3", "#D4B5FF", "#A9DEF9", "#B39CD0",
            "#FFC3A0", "#FFAFCC", "#B5EAD7", "#FFCBD5", "#E6E6FA",
            "#EAD1DC", "#FFDFBA", "#C5C1FF", "#FFD6E8", "#B0E57C",
            "#A8E6CF", "#D5C3FF", "#F8B195", "#F67280", "#C06C84",
            "#6C5B7B", "#355C7D", "#FFB07C", "#F8C8DC", "#FFAAA5",
        ];

        // If the index exceeds the predefined colors, generate a new color
        if (index >= colors.length) {
            let newColor;
            do {
                newColor = generateMutedColor();
            } while (colors.includes(newColor) || newColor === '#000000' || newColor === '#FFFFFF');
            return newColor;
        }

        return colors[index];
    }

    function generateColorArray(length, index) {
        if (length) {
            const colorArray = [];
            for (let i = 0; i < length; i++) {
                colorArray.push(getColorByIndex(i));
            }
            return colorArray;
        }
        return getColorByIndex(index);
    }

    return (
        <div
            className="db-grid-item"
            style={{
                gridColumn: `span ${item.column || 1}`,
                gridRow: `span ${item.row || 1}`,
                opacity: opacity
            }}
            ref={ref}
        >
            {widgetData?.content
                ? handleFindWidget(widgetData?.content)
                : <div className='db-create-loading-page'><CircularProgress /></div>}
            <MoreVertIcon className='db-grid-item-edit-button' onClick={handleClick} />
            {openConfigure ? <OpenWithIcon className='db-grid-item-move-icon' /> : ""}
            <Popper open={open} anchorEl={anchorEl} style={{ marginTop: 10, minWidth: 90 }} transition placement='bottom-end'>
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Paper className='db-widget-popper'>
                                <div className='db-widget-popper-item-container' onClick={() => handleAddWidget(item.id, item.order)}>
                                    <span>{t(label + "EDIT")}</span>
                                    <EditIcon className='db-widget-configuration-icon db-widget-edit-icon' />
                                </div>
                                <div className='db-widget-popper-item-container' onClick={() => handleOpenRemoveWidget(true, item)}>
                                    <span>{t(label + "DELETE")}</span>
                                    <DeleteIcon className='db-widget-configuration-icon db-widget-delete-icon' />
                                </div>
                            </Paper>
                        </Fade>
                    </ClickAwayListener>
                )
                }
            </Popper >
        </div>
    )
}

export default Widget